import { Button } from "@/src/components/buttons";
import { Card } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { InfiniteFetchableList } from "@/src/components/fetchableLists";
import SearchKeyHighliter from "@/src/components/seachkeyHighlighter";
import {
  Exam,
  ExamGroup,
  FolderItem,
  frequentSnomedParents,
  Patient,
  Reference,
  ReferenceCategory,
  ReferencesGroup,
  SnomedConcept,
  Upload,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, getUserId, uid } from "@/src/utils/utils";
import { useState } from "react";
import { openReferenceGroupForm } from "../admin/referentiel/referenceGroupForm";
import { getStandardConfidentiality } from "../patients/confidentialitiesHook";
import SnomedConceptCard from "../snomed/snomedConceptCard";
import { openExamDateDialog } from "./examDateDialog";
import { USER } from "@/src/data/globalVariables";

export function ExamsChoiceList({
  defaultReferences,
  categoryId,
  onSelect,
}: {
  defaultReferences?: (Reference | null | undefined)[] | null | undefined;
  categoryId?: ReferenceCategory | null | undefined;
  onSelect: (
    newValue: Reference[],
    group: ReferencesGroup | null | undefined
  ) => void;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState<string>("");

  function getFrequentReferencesSnomed() {
    return USER.get()
      ?.speciality?.frequentReferences?.filter(
        (reference) =>
          (
            [
              "exam",
              "biology",
              "imaging",
              "ecg",
              "exploration",
              "other",
            ] as ReferenceCategory[]
          ).includes(reference?.category || "exam") && reference?.snomedConcept
      )
      .map((reference) => {
        return reference?.snomedConcept;
      }) as SnomedConcept[];
  }

  function localOnSelect(
    references: (Reference | null | undefined)[],
    group: ReferencesGroup | null | undefined
  ) {
    let filteredReferences = references.filter(
      (reference) => reference != null
    );
    for (let reference of filteredReferences) {
      apiFetch("references", "incrementReferenceCount", {
        referenceId: reference.id!,
      });
    }
    onSelect(filteredReferences, group);
  }

  return (
    <div className="c1 gap10">
      <InfiniteFetchableList
        queryKey={[
          "ExamsChoiceList",
          searchKey,
          reload,
          JSON.stringify([
            frequentSnomedParents.procedure.id,
            frequentSnomedParents.labExam.id,
            frequentSnomedParents.substance.id,
            frequentSnomedParents.intervention.id,
          ]),
          categoryId,
        ]}
        limit={40}
        getFunction={async (limit, offset, searchKey) => {
          let result = await apiFetch(
            "snomedConcepts",
            "loadExamSnomedConcepts",
            {
              userId: getUserId(),

              searchKey,
              parentIds: [
                frequentSnomedParents.procedure.id,
                frequentSnomedParents.labExam.id,
                frequentSnomedParents.substance.id,
                frequentSnomedParents.intervention.id,
              ],
              limit,
              offset,
            },
            "Erreur lors du chargement des motifs"
          );
          if ((searchKey?.length || 0) < 3) {
            console.log("getFrequentReferencesSnomed()",getFrequentReferencesSnomed())
            return [...result, ...getFrequentReferencesSnomed()];
          }
          return result;
        }}
        searchKey={searchKey}
        filtersComponent={
          <Button
            type="tertiary"
            onClick={async () => {
              let group: ReferencesGroup = {
                name: `${searchKey || ""}`,
                creator: getUserId(),
                visibility: "user",
                type: "exams",
                usageCount: 0,
                references: [],
              };
              await openReferenceGroupForm({
                current: group,
                onSave: async (newValue) => {
                  setReload((prev) => prev + 1);
                  setSearchKey("");
                  localOnSelect(newValue.references || [], newValue);
                },
                types: ["exams"],
              });
            }}
          >
            Nouveau groupe
          </Button>
        }
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
        emptyChild={
          <div className="grid c1 gap10">
            {searchKey?.length < 3 ? (
              <div className=" p20 tcenter">
                Veuillez saisir au moins <strong>3 lettres</strong>
              </div>
            ) : (
              <div className="gray p20 center">
                Aucun élément trouvé
                <strong>{searchKey && `content ${searchKey}`}</strong>
              </div>
            )}
          </div>
        }
      >
        {(data) => {
          const concepts = data?.filter((item) =>
            item.id?.includes("concepts")
          ) as SnomedConcept[];
          const groups = data?.filter((item) =>
            item.id?.includes("referencesGroup")
          ) as ReferencesGroup[];

          return (
            <div className=" c1 gap10">
              {groups?.map((group) => {
                return (
                  <Card
                    key={uid()}
                    color="purple"
                    className="empty"
                    trailing={
                      <div className="gray">{group.usageCount || "-"}</div>
                    }
                    title={
                      <div>
                        <span className="bold">
                          <SearchKeyHighliter searchKey={searchKey}>
                            {group.name}
                          </SearchKeyHighliter>
                        </span>{" "}
                      </div>
                    }
                    onClick={async () => {
                      localOnSelect(group.references || [], group);
                    }}
                  >
                    <div>
                      <SearchKeyHighliter searchKey={searchKey}>
                        {group.references
                          ?.map((reference) => reference?.name)
                          ?.join(", ")}
                      </SearchKeyHighliter>
                    </div>
                  </Card>
                );
              })}
              <div className="grid c1 gap10 ">
                {concepts.map((concept) => {
                  return (
                    <SnomedConceptCard
                      key={uid()}
                      searchKey={searchKey || ""}
                      filteredParentIds={[
                        "procedure",
                        "labExam",
                        "substance",
                        "intervention",
                      ]}
                      concept={concept}
                      onClick={async () => {
                        const reference = await apiFetch(
                          "references",
                          "upsertUsingSnomedConcept",
                          {
                            conceptId: getItemId(concept),
                            category: categoryId || "exam",
                            postCoordinationConceptIds: [],
                          }
                        );
                        localOnSelect([reference], undefined);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        }}
      </InfiniteFetchableList>
    </div>
  );
}

export async function openExamsChoiceList({
  categoryId,
  patient,
  parent,
  onConfirm,
}: {
  categoryId: ReferenceCategory | null | undefined;
  patient: Patient;
  parent: FolderItem | null | undefined;
  onConfirm?: (exam: Exam[]) => void;
}) {
  await openDrawer({
    id: "examChoiceDialog",
    title: "Choisir un examen",
    content: (
      <ExamsChoiceList
        categoryId={categoryId}
        onSelect={async (references, referencesGroup) => {
          
          await openExamDateDialog({
            currentDate: null,
            currentReferences: references,
            onConfirm: async (date, references) => {
              let exams: Exam[] = [];
              let examGroup: ExamGroup = {
                type: "examGroup",
                creator: getUserId(),
                parent,
                patient,
                referencesGroup,
              };

              if (referencesGroup) {
                examGroup = await apiFetch("examGroups", "addOne", {
                  item: examGroup,
                });
              }

              for (let reference of references) {
                let exam: Exam = {
                  confidentiality: getStandardConfidentiality(patient),
                  readBy: [getUserId()],
                  type: "exam",
                  creator: getUserId(),
                  examGroup: getItemId(examGroup),
                  patient: patient,
                  uploads: [] as Upload[],
                  status: "pending",
                  parent,
                  notes: "",
                  conclusion: "",
                  reference,
                  content: {
                    patient: patient,
                    reference,
                    fieldAnswers: [],
                    creator: getUserId(),
                  },
                  _date: date,
                };

                console.log("gonna save", exam);
                let item = await apiFetch(
                  "exams",
                  "addOne",
                  { exam },
                  "Erreur lors de la création de l'examen"
                );
                exams.push(item);
              }
              closeDrawer("examChoiceDialog");
              onConfirm?.(exams);
            },
          });
        }}
      />
    ),
    footer: <div></div>,
  });
}
